import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { asyncScheduler, ReplaySubject } from 'rxjs';
import { tap, throttleTime } from 'rxjs/operators';

import { AutoSub, AutoSubOnDestroy } from '../../providers';
import { Coords } from '../../models';
import { GeoSearchService, PlaceResult } from './geo-search.service';
import { Animations } from '../../animations';

@AutoSubOnDestroy()
@Component({
    selector: 'geo-search',
    templateUrl: './geo-search.component.html',
    styleUrls: ['./geo-search.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.shrinkExpandVertical(100, 63), Animations.fadeIn(100)]
})
export class GeoSearchComponent implements OnInit {

    @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

    @Input('initQuery') set setQuery(query: string) {
        // Reset geo search
        this.addressList = [];
        this.initQuery = query;
        this.selectedResult = null;
        this.searchAddress(query);
    }

    /** Set true to include long and lat inputs under search results */
    @Input() includeCoordinates: boolean = false;
    @Input() proximityCoords: Coords = null;
    /** Only search for cities and exclude any other search results */
    @Input() exclusiveCitySearch: boolean = false;
    @Output() resultSelected = new EventEmitter<PlaceResult>();

    /** Initial address input */
    public initQuery: string;
    /** Current address input  */
    public lastQuery: string = "";
    /** The list of search results when an address is input */
    public addressList: PlaceResult[] = [];
    /** Valid coords input or selected result from address list */
    public selectedResult: PlaceResult = null;
    /** Show/hide coordinates inputs */
    public showCoordinates: boolean = false;
    /** Input coords longitude */
    public long: number;
    /** Input coords latitude */
    public lat: number;
    /** Keeps track of which drawer should be opened ('search' or 'selected') */
    public activeContent: 'search' | 'selected' = 'search';
    /** Tracks whether address search input is in focus or not */
    public addressInputFocus: boolean = false;
    /** Msg to be shown when coords are input but not valid */
    public coordsError: string[] = [];
    /** The highlighted, but not selected, address in address list */
    public activeAddress: PlaceResult = null;
    /** The index belonging to activeAddress in address list */
    private activeIdx: number = 0;
    /** Opens drawer with active content or closes both drawers */
    public isDrawerOpen: boolean = false;
    /** Show loading while getting results from server */
    public isLoadingResults: boolean = false;

    /** Outputs whether drawer is open or closed */
    @Output() isDrawerOpenChange = new EventEmitter<boolean>();

    constructor(private geoSearchService: GeoSearchService) {
    }

    private querySubject: ReplaySubject<string> = new ReplaySubject<string>();

    ngOnInit() {
        const updateLoading = (query : string) => {
            this.isLoadingResults = (query && query.length >= 2) ? true : false;
        }

        AutoSub(this).reg['searchSub'] = this.querySubject.pipe(tap((query) => updateLoading(query)), throttleTime(1000, asyncScheduler, { leading: false, trailing: true })).subscribe(async (query) => {
            // Start search if query is longer than two chars
            if(!query && query.length < 2) {
                this.addressList = [];
                this.lastQuery = "";
                this.isLoadingResults = false;
                return;
            }
            // Search only for cities or any type of place
            const searchType = this.exclusiveCitySearch ? 'locality' : '';
            this.lastQuery = query;
            this.addressList = (await this.geoSearchService.searchPlaces(query, this.proximityCoords, null, searchType)).slice(0,6);
            this.activeIdx = 0;
            this.activeAddress = this.addressList.length > 0 ? this.addressList[0] : null;
            this.isLoadingResults = false;
        });
    }

    /**
     * Perform a search programmatically from external component,
     * using either a search query or coordinates.
     */
    public async performSearch(query: string, coords?: Coords) {
        // Reset current search results
        this.clear();
        // Clear show loading result
        this.isLoadingResults = false;

        // Perform a query search
        if(query) this.querySubject.next(query);
        else this.lastQuery = '';
        // Perform a search with coordinates
        if(coords) {
            this.lat = coords.lat;
            this.long = coords.long;
            await this.validateCoords();
        }

        // Focus input and open search result
        this.focusSearchInput();
    }

    public onSearchInputChange(ev: Event) {
        this.activeContent = 'search';
        let input = ev.target as HTMLInputElement;
        this.toggleOpen(true);
        
        // Check if input contains valid coordinates
        const coords = this.detectAndParseCoordinates(input.value);
        if (coords) {
            // Coordinates detected - switch to coordinate mode and search
            if (this.includeCoordinates) {
                this.showCoordinates = true;
            }
            this.lat = coords.lat;
            this.long = coords.lng;
            this.validateCoords();
            // Skip address search when coordinates are detected
            return;
        }
        
        // No coordinates detected - proceed with normal address search
        this.searchAddress(input.value);
    }

    /**
     * Validates coordinate values and returns them as an object if valid, null otherwise.
     */
    private validateCoordinateValues(lat: number, lng: number): { lat: number, lng: number } | null {
        if (isNaN(lat) || isNaN(lng) || 
            lat < -90 || lat > 90 || 
            lng < -180 || lng > 180) {
            return null;
        }
        return { lat, lng };
    }

    /**
     * Detects and parses coordinates from input string.
     * Supports comma-separated format (lat, lng) and GeoJSON array format [lng, lat, ...].
     * Returns parsed coordinates if valid, null otherwise.
     */
    private detectAndParseCoordinates(input: string): { lat: number, lng: number } | null {
        if (!input || typeof input !== 'string') {
            return null;
        }

        const trimmed = input.trim();
        
        // Try GeoJSON array format: [lng, lat, ...] (reversed to lat, lng)
        const geojsonMatch = trimmed.match(/^\s*\[\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
        if (geojsonMatch) {
            const lng = parseFloat(geojsonMatch[1]);
            const lat = parseFloat(geojsonMatch[2]);
            // GeoJSON format is [lng, lat], reverse to [lat, lng]
            return this.validateCoordinateValues(lat, lng);
        }
        
        // Try comma-separated format: lat, lng
        const commaMatch = trimmed.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
        if (commaMatch) {
            const lat = parseFloat(commaMatch[1]);
            const lng = parseFloat(commaMatch[2]);
            return this.validateCoordinateValues(lat, lng);
        }
        
        return null;
    }

    private searchAddress(query: string) {
        this.querySubject.next(query);
    }

    public selectResult(feature: PlaceResult) {
        // Set result (address or coords) in address input and emit
        this.selectedResult = feature;
        this.resultSelected.emit(feature);
        if(feature) this.activeContent = 'selected';
    }

    public activateAddress(feature: PlaceResult, idx?: number) {
        // Focus on address in address list
        this.activeAddress = feature;
        this.activeIdx = idx || 0;
    }

    public async closeAndClear() : Promise<void> {
        this.toggleOpen(false);
        // Wait for drawer closing animation to reset properties
        await new Promise<void>(resolve => setTimeout(() => resolve(), 300));
        this.activeContent = 'search';
        this.selectResult(null);
        this.clear();
    }

    private clear() {
        this.addressList = [];
        this.lastQuery = "";
        this.showCoordinates = false;
        this.lat = null;
        this.long = null;
        this.coordsError = [];
        this.searchInput.nativeElement.value = '';
    }

    public focusSearchInput() {
        this.searchInput.nativeElement.focus();
        this.searchInput.nativeElement.click();
    }

    public toggleCoordinates(show: boolean) {
        this.showCoordinates = show;
        // Remove focus from address list
        if(this.showCoordinates) this.activeAddress = null;
        // Get back focus on address list
        else if(this.addressList.length > this.activeIdx) this.activeAddress = this.addressList[this.activeIdx];
    }

    public async validateCoords() {
        // Check if coords are valid
        this.coordsError = [];
        if(typeof this.long == 'undefined' || isNaN(this.long) || this.long > 180 || this.long < -180) return this.coordsError.push($localize`Make sure longitude is between -180 and 180.`);
        if(typeof this.lat == 'undefined' || isNaN(this.lat) || this.lat > 90 || this.lat < -90) return this.coordsError.push($localize`Make sure latitude is between -180 and 180.`);

        // Convert coords to address and select
        const addressInfo = await this.geoSearchService.getAddress(new Coords(this.lat, this.long));

        // Generate a feature result with the given coordinates in case no address could be found
        const result: PlaceResult = {
            geometry: { location: { lng: this.long, lat: this.lat } },
            position: new Coords(this.lat, this.long),
            formatted_address: addressInfo?.address || $localize`Unknown address`,
            name: addressInfo?.name || $localize`In the rough`,
            photos: []
        };

        // Clear address list
        this.addressList = [];
        this.lastQuery = result?.name || "";
        this.selectResult(result);
    }

    public onKeyDown(event: KeyboardEvent) {
        switch(event.key) {
            // Select address or coords (depending on focus)
            case "Enter":
                if(this.activeAddress) this.selectResult(this.activeAddress);
                else this.validateCoords();
                break;
            // Close drawer
            case 'Escape':
                this.toggleOpen(false);
                break;
            // Navigate address list (depending on focus)
            case 'ArrowDown':
            case 'ArrowUp':
                if(!this.activateAddress || this.addressList.length <= 0) return;
                // Detect direction
                let direction = (event.code == "ArrowDown") ? +1 : -1;
                this.activeIdx = (this.activeIdx + direction) % this.addressList.length;

                if(this.activeIdx < 0) this.activeIdx = this.addressList.length - 1;
                this.activeAddress = this.addressList[this.activeIdx];
                break;
        }
    }

    public toggleOpen(open: boolean) {
        this.isDrawerOpen = open;
        this.isDrawerOpenChange.emit(this.isDrawerOpen);
    }

}
